/// <amd-module name="TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Components/StatsSection" />

import * as React from "react";
import { ComponentRegionNames, ImagePaths, OverviewTabTelemetryConstants } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Constants";
import { getImageLocation } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Utils";
import * as Resources from "TFS/Views/ProjectOverviewPage/Resources";
import { getId } from "VSS/Core/Util/Object";
import { IVssComponentContext, IVssComponentProperties, IVssLayoutManager, RegionComponent, VssComponent } from "VSS/Platform/Layout";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Card } from "VSSUI/Card";
import { Dropdown, DropdownExpandableButton } from "VSSUI/Dropdown";
import { Image } from "VSSUI/Image";
import { IListBoxItem } from "VSSUI/ListBox";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { IListSelection } from "VSSUI/List";

const defaultNumberOfDaysSelected = 7;

export interface StatsSectionState {
    numberOfDaysSelected: number;
}

export class StatsSection extends VssComponent<IVssComponentProperties, StatsSectionState> {
    private _statsTitleId: string;
    private dropdownOptions: IListBoxItem[] = [
        { id: "1", text: Resources.LastOneDay },
        { id: "7", text: Resources.LastSevenDays },
        { id: "30", text: Resources.LastThirtyDays }
    ];
    private numberOfDaysSelection = new DropdownSelection();

    constructor(props: IVssComponentProperties, context: IVssComponentContext) {
        super(props, context);

        this._statsTitleId = getId("statsTitleId");
        this.numberOfDaysSelection.select(1);

        this.state = {
            numberOfDaysSelected: defaultNumberOfDaysSelected
        };
    }

    public render(): JSX.Element {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        const childComponents = layoutManager.getComponentsForRegion(ComponentRegionNames.StatsRegion);

        return (
            <Card className={"project-overview-card"}>
                <div className="stats-container-card project-overview-card-container" role="region" aria-labelledby={this._statsTitleId}>
                    <div className="title-and-dropdown flex">
                        <div className="title-m word-break" role="heading" aria-level={2} id={this._statsTitleId}>
                            {Resources.ProjectStats}
                        </div>
                        {childComponents.length > 0 && (
                            <div className="days-dropdown">
                                <Dropdown
                                    items={this.dropdownOptions}
                                    ariaLabel={Resources.StatsDropdownAriaLabel}
                                    onSelect={this._numberOfDaysChanged}
                                    selection={this.numberOfDaysSelection}
                                    renderExpandable={props => (
                                        <DropdownExpandableButton
                                            {...props}
                                            renderSelectedItems={(selection: IListSelection, items: IListBoxItem[]) => {
                                                const item = items[selection.value[0].beginIndex];

                                                return (
                                                    <>
                                                        <span className="secondary-text">{Resources.ProjectStats_Period}</span>
                                                        {item.text}
                                                    </>
                                                );
                                            }}
                                        />
                                    )}
                                />
                            </div>
                        )}
                    </div>
                    <RegionComponent
                        componentRegion={ComponentRegionNames.StatsRegion}
                        childComponentProperties={{ numOfDays: this.state.numberOfDaysSelected, className: "metric-tile" }}
                        fallbackComponent={this._getDayZeroComponent}
                    />
                </div>
            </Card>
        );
    }

    private _numberOfDaysChanged = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, numberOfDaysOption: IListBoxItem) => {
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(OverviewTabTelemetryConstants.Area, OverviewTabTelemetryConstants.ProjectStats_NumberOfDaysDropDown, {
            NumberOfDaysSelected: numberOfDaysOption.id
        });

        this.setState({
            numberOfDaysSelected: Number(numberOfDaysOption.id)
        });
    };

    private _getDayZeroComponent = (): JSX.Element => {
        return (
            <div className="stats-upsell flex-column flex-center">
                <Image containImage={true} className="image" src={getImageLocation(this.context.pageContext, ImagePaths.StatsUpsellPath)} alt="" />
                <div className="message title-xs">{Resources.StatsUpsellMessage}</div>
                <div className="help-text font-size-m">{Resources.StatsUpsellHelpText}</div>
            </div>
        );
    };
}
